/** Customer actions follow the same messaging limits the server enforces. */
export type CustomerReview = {
  status: string;
  proposal_state?: string;
  payment_state?: string;
  closed_at?: string | null;
  assigned_attorney_id?: string | null;
  attorney_status?: string | null;
  has_card?: boolean;
  latest_client_seq?: string | number;
  attorney_replied_through?: string | number;
  latest_attorney_seq?: string | number;
  client_replied_through?: string | number;
  clarification_deadline?: string | null;
  clarification_round_limit?: number | null;
  clarification_rounds_used?: number;
  needs_reply?: boolean;
  due_at?: string;
};

export function customerFollowUpExpired(
  r: Pick<CustomerReview, "clarification_deadline">,
  now = Date.now()
) {
  return Boolean(
    r.clarification_deadline &&
      new Date(r.clarification_deadline).getTime() <= now
  );
}

export function customerCanMessage(r: CustomerReview, now = Date.now()) {
  if (r.closed_at || r.status === "cancelled") return false;
  if (r.status === "queued" || r.assigned_attorney_id === null) return false;
  if (r.attorney_status != null && r.attorney_status !== "approved")
    return false;
  if (r.status !== "completed" || r.clarification_round_limit == null)
    return true;
  if (customerFollowUpExpired(r, now)) return false;
  return (
    Number(r.clarification_rounds_used || 0) < r.clarification_round_limit ||
    Number(r.latest_client_seq || 0) > Number(r.attorney_replied_through || 0)
  );
}

export function customerNeedsReply(r: CustomerReview, now = Date.now()) {
  const unread =
    r.latest_attorney_seq !== undefined
      ? Number(r.latest_attorney_seq) > Number(r.client_replied_through || 0)
      : Boolean(r.needs_reply);
  return unread && customerCanMessage(r, now);
}

export function customerWorkflow(r: CustomerReview, now = Date.now()) {
  const state = (
    key: string,
    label: string,
    heading: string,
    explanation: string,
    action: "proposal" | "discussion" | "answers" | null = null,
    actionLabel = "",
    needsAction = false
  ) => ({ key, label, heading, explanation, action, actionLabel, needsAction });
  if (r.status === "cancelled")
    return state(
      "cancelled",
      "Cancelled",
      "Request cancelled",
      "Your question and submitted materials remain available."
    );
  if (r.closed_at)
    return state(
      "closed",
      "Closed",
      "Review closed",
      "Your answer and messages remain available. Messaging has ended.",
      "answers",
      "Read answer"
    );
  if (
    r.assigned_attorney_id &&
    r.attorney_status != null &&
    r.attorney_status !== "approved"
  )
    return state(
      "assignment_paused",
      "Assignment being reviewed",
      "We’re reviewing your attorney assignment",
      "Messages and approval are paused while the assignment is reviewed. We’ll notify you when you can continue."
    );
  if (r.proposal_state === "awaiting_client")
    return state(
      "approval",
      "Approval needed",
      "Review your attorney’s proposed changes",
      "Check the scope, price, and turnaround before accepting. You can decline without being charged.",
      "proposal",
      "Review changes",
      true
    );
  if (r.proposal_state === "agreed" && r.payment_state === "awaiting_payment")
    return state(
      "payment",
      "Payment needed",
      "Pay to start your review",
      "The terms are agreed. Work and the answer clock start only after payment succeeds.",
      "proposal",
      "Review payment",
      true
    );
  if (r.payment_state === "unpaid" && r.has_card === false)
    return state(
      "card",
      "Card needed",
      "Save a card for this request",
      "No charge now. Your card is charged the agreed price when an attorney accepts, or when you approve revised terms.",
      "proposal",
      "Add card",
      true
    );
  if (["declined", "needs_revision"].includes(r.proposal_state || ""))
    return state(
      "terms",
      "Waiting for updated terms",
      "Waiting for your attorney to update the terms",
      "Nothing is charged while the terms are being updated. You can discuss the request or cancel before work starts."
    );
  if (customerNeedsReply(r, now))
    return state(
      "reply",
      "Reply needed",
      "Your attorney sent you a message",
      "Read the message and reply here. Messages in this review are shared directly with your attorney.",
      "discussion",
      "Read and reply",
      true
    );
  if (r.status === "completed") {
    if (
      Number(r.latest_client_seq || 0) > Number(r.attorney_replied_through || 0)
    )
      return state(
        "follow_up_pending",
        customerFollowUpExpired(r, now)
          ? "Follow-up window ended"
          : "Waiting for follow-up reply",
        customerFollowUpExpired(r, now)
          ? "The follow-up window has ended"
          : "Your follow-up is with your attorney",
        customerFollowUpExpired(r, now)
          ? "Your follow-up was sent. Read your answer and messages for any reply; you can’t add more messages to this review."
          : "You’re waiting for their reply. You can read your answer and messages while you wait.",
        "discussion",
        "View messages"
      );
    if (!customerCanMessage(r, now)) {
      const expired = customerFollowUpExpired(r, now);
      return state(
        "follow_up_ended",
        expired
          ? "Follow-up window ended"
          : r.clarification_round_limit === 0
            ? "Answered"
            : "Follow-up answered",
        expired
          ? "The follow-up window has ended"
          : r.clarification_round_limit === 0
            ? "Your attorney’s answer is ready"
            : "Your included follow-up is complete",
        "Read your answer and messages, then close the review when you have what you need.",
        "answers",
        "Read answer"
      );
    }
    return state(
      "answered",
      "Answered",
      "Your attorney’s answer is ready",
      "Read the written answer. You can ask an included follow-up about it, or close the review when you have what you need.",
      "answers",
      "Read answer"
    );
  }
  if (r.status === "in_progress")
    return state(
      "working",
      "Attorney working",
      "Your attorney is preparing your answer",
      "Nothing is needed from you right now. We’ll notify you when the answer arrives or your attorney needs information."
    );
  if (r.status === "assigned")
    return state(
      "matched",
      "Attorney reviewing request",
      "Your attorney is checking your request",
      "They will accept the agreed terms or suggest changes. The answer clock has not started yet."
    );
  return state(
    "matching",
    "Finding your attorney",
    "Request received — finding your attorney",
    "Matching happens before the answer clock starts. We’ll notify you when an attorney accepts or proposes changes. You can cancel before work starts."
  );
}
